import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock,
  FileText,
  FileUp,
  Handshake,
  Plus,
  Receipt,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { apiFetch, mensagemDeErro } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

import { BoletoDialog, abrirBoletoPdf } from './financial/BoletoDialog';
import { ExtratoBancario } from './financial/ExtratoBancario';

type FinancialStatus = 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
type FinancialType = 'INCOME' | 'EXPENSE';

interface FinancialEntry {
  id: string;
  tipo: FinancialType;
  valor: string | number;
  dataVencimento: string;
  dataPagamento?: string | null;
  status: FinancialStatus;
  referencia: string;
  numeroParcela?: number | null;
  totalParcelas?: number | null;
  client?: { id: string; razaoSocial: string } | null;
  contract?: { id: string; codigoContrato: string } | null;
  /** Cobranca viva (boleto registrado), quando ha. */
  charges?: { id: string; providerName: string; linhaDigitavel?: string | null }[];
}

interface ListResponse {
  rows: FinancialEntry[];
  total: number;
}

interface Summary {
  receitasPagas: number;
  despesasPagas: number;
  aReceber: number;
  emAtraso: number;
}

const STATUS_LABEL: Record<FinancialStatus, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  OVERDUE: 'Em atraso',
  CANCELLED: 'Cancelado',
};

const STATUS_STYLE: Record<FinancialStatus, string> = {
  PENDING: 'bg-amber-500/10 text-amber-600',
  PAID: 'bg-emerald-500/10 text-emerald-600',
  OVERDUE: 'bg-destructive/10 text-destructive',
  CANCELLED: 'bg-muted text-muted-foreground line-through',
};

function fmtDate(iso: string): string {
  // Datas sem hora (vencimento) chegam como "2026-09-23T00:00:00.000Z".
  // Passar por Date aplica o fuso (UTC-3) e mostra 22/09 — um dia antes do
  // vencimento real. Nesses casos o dia e lido direto da string.
  const m = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.000)?Z$/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

/** Linhas por pagina. O servidor aceita ate 200; 100 cabe numa tela sem virar rolagem infinita. */
const PAGE_SIZE = 100;

type Ordenacao = { campo: string; dir: 'asc' | 'desc' };

/** Cabecalho clicavel: primeiro clique ordena crescente, segundo inverte. */
function ThOrdenavel({
  campo,
  atual,
  onClick,
  children,
}: {
  campo: string;
  atual: Ordenacao;
  onClick: (campo: string) => void;
  children: ReactNode;
}) {
  const ativo = atual.campo === campo;
  return (
    <th className="px-4 py-2 font-medium">
      <button
        type="button"
        onClick={() => onClick(campo)}
        className={`inline-flex items-center gap-1 hover:underline ${ativo ? 'text-foreground' : ''}`}
      >
        {children}
        {ativo ? (
          atual.dir === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}

/** Abre o painel do boleto vivo do lancamento. Componente proprio porque
 *  dentro do onClick o TS perde a certeza de que `charges[0]` existe. */
function BotaoBoleto({
  boleto,
  onOpen,
}: {
  boleto: { id: string; providerName: string };
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      data-dica={`Boleto ${boleto.providerName} — abrir, prorrogar, baixar`}
      onClick={onOpen}
      className="rounded-md p-2 text-primary hover:bg-primary/10"
    >
      <FileText className="h-4 w-4" />
    </button>
  );
}

export function FinancialPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [tab, setTab] = useState<'entries' | 'conciliacao'>('entries');
  const [tipoFilter, setTipoFilter] = useState<FinancialType | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<FinancialStatus | 'ALL'>('ALL');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [vencDe, setVencDe] = useState('');
  const [vencAte, setVencAte] = useState('');
  const [sort, setSort] = useState<Ordenacao>({ campo: 'dataVencimento', dir: 'asc' });
  const [page, setPage] = useState(1);
  // A busca so vai ao servidor depois de a pessoa parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);
  function ordenarPor(campo: string) {
    setSort((s) => ({ campo, dir: s.campo === campo && s.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  }
  const [showCreate, setShowCreate] = useState(false);
  const [chargeFor, setChargeFor] = useState<FinancialEntry | null>(null);
  const [boletoFor, setBoletoFor] = useState<FinancialEntry | null>(null);
  const [showCnabImport, setShowCnabImport] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);

  const baseQuery = (params: URLSearchParams) =>
    apiFetch<ListResponse>(`/financial?${params}`);

  const { data, isLoading } = useQuery({
    queryKey: [
      'financial',
      { tab, tipoFilter, statusFilter, buscaAplicada, vencDe, vencAte, sort, page },
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: String(PAGE_SIZE),
        page: String(page),
        sort: sort.campo,
        dir: sort.dir,
      });
      if (buscaAplicada) params.set('search', buscaAplicada);
      if (vencDe) params.set('from', vencDe);
      if (vencAte) params.set('to', vencAte);
      if (tipoFilter !== 'ALL') params.set('tipo', tipoFilter);
      if (statusFilter !== 'ALL') {
        params.set('status', statusFilter);
      } else if (tab === 'conciliacao') {
        params.set('status', 'PENDING,OVERDUE');
      }
      return baseQuery(params);
    },
  });

  const summary = useQuery({
    queryKey: ['financial', 'summary'],
    queryFn: () => apiFetch<Summary>('/financial/summary'),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) =>
      apiFetch<FinancialEntry>(`/financial/${id}/pay`, {
        method: 'PATCH',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['financial'] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Financeiro</h1>
          <p className="text-sm text-muted-foreground">
            Lançamentos de receita e despesa. Pendentes com vencimento passado viram "Em atraso"
            automaticamente às 02:00.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setShowCnabImport(true)}
                title="Importar arquivo de retorno bancario (.RET) pra conciliar boletos pagos"
                className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
              >
                <FileUp className="h-4 w-4" /> Importar CNAB
              </button>
              <button
                type="button"
                onClick={() => setShowDistribute(true)}
                title="Apurar lucro do periodo e dividir entre os socios pela %"
                className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
              >
                <Handshake className="h-4 w-4" /> Distribuir lucros
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo lançamento
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <SummaryCard
          icon={<TrendingUp className="h-4 w-4" />}
          tone="good"
          label="Receitas pagas (mês)"
          value={formatBRL(summary.data?.receitasPagas ?? 0)}
        />
        <SummaryCard
          icon={<TrendingDown className="h-4 w-4" />}
          tone="warn"
          label="Despesas pagas (mês)"
          value={formatBRL(summary.data?.despesasPagas ?? 0)}
        />
        <SummaryCard
          icon={<Clock className="h-4 w-4" />}
          tone="info"
          label="A receber (mês)"
          value={formatBRL(summary.data?.aReceber ?? 0)}
        />
        <SummaryCard
          icon={<AlertCircle className="h-4 w-4" />}
          tone="bad"
          label="Em atraso (mês)"
          value={formatBRL(summary.data?.emAtraso ?? 0)}
        />
      </section>

      <div className="flex gap-2 border-b border-border">
        <TabButton active={tab === 'entries'} onClick={() => setTab('entries')}>
          Lançamentos
        </TabButton>
        <TabButton active={tab === 'conciliacao'} onClick={() => setTab('conciliacao')}>
          Conciliação
        </TabButton>
      </div>

      {tab === 'entries' && (
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={tipoFilter}
            onChange={(e) => {
              setTipoFilter(e.target.value as FinancialType | 'ALL');
              setPage(1);
            }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="ALL">Todos tipos</option>
            <option value="INCOME">Entrada</option>
            <option value="EXPENSE">Saída</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as FinancialStatus | 'ALL');
              setPage(1);
            }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="ALL">Todos status</option>
            {(Object.keys(STATUS_LABEL) as FinancialStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Buscar cliente, referência, contrato…"
            className="input min-w-[260px]"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            Venc. de
            <input
              type="date"
              className="input"
              value={vencDe}
              onChange={(e) => {
                setVencDe(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            até
            <input
              type="date"
              className="input"
              value={vencAte}
              onChange={(e) => {
                setVencAte(e.target.value);
                setPage(1);
              }}
            />
          </label>
          {(busca || vencDe || vencAte || tipoFilter !== 'ALL' || statusFilter !== 'ALL') && (
            <button
              type="button"
              onClick={() => {
                setBusca('');
                setVencDe('');
                setVencAte('');
                setTipoFilter('ALL');
                setStatusFilter('ALL');
                setPage(1);
              }}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              limpar filtros
            </button>
          )}
          {data && (
            <span className="ml-auto text-xs text-muted-foreground">
              {data.total} lançamento(s)
              {data.total > data.rows.length &&
                ` · mostrando ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + data.rows.length}`}
            </span>
          )}
        </div>
      )}

      {tab === 'conciliacao' && (
        <ExtratoBancario
          canManage={canManage}
          onChanged={() => qc.invalidateQueries({ queryKey: ['financial'] })}
        />
      )}

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {data && data.rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <CircleDollarSign className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {tab === 'conciliacao' ? 'Sem lançamentos a conciliar.' : 'Sem lançamentos.'}
            </p>
          </div>
        )}
        {data && data.rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Tipo</th>
                <ThOrdenavel campo="cliente" atual={sort} onClick={ordenarPor}>
                  Cliente / Contrato
                </ThOrdenavel>
                <ThOrdenavel campo="referencia" atual={sort} onClick={ordenarPor}>
                  Referência
                </ThOrdenavel>
                <ThOrdenavel campo="dataVencimento" atual={sort} onClick={ordenarPor}>
                  Vencimento
                </ThOrdenavel>
                <ThOrdenavel campo="valor" atual={sort} onClick={ordenarPor}>
                  Valor
                </ThOrdenavel>
                <ThOrdenavel campo="status" atual={sort} onClick={ordenarPor}>
                  Status
                </ThOrdenavel>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((e) => (
                <tr key={e.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                        e.tipo === 'INCOME'
                          ? 'bg-emerald-500/10 text-emerald-600'
                          : 'bg-amber-500/10 text-amber-600'
                      }`}
                    >
                      {e.tipo === 'INCOME' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {e.tipo === 'INCOME' ? 'Entrada' : 'Saída'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {e.client && (
                      <div className="font-medium text-xs">{e.client.razaoSocial}</div>
                    )}
                    {e.contract && (
                      <div className="text-xs text-muted-foreground font-mono">
                        {e.contract.codigoContrato}
                      </div>
                    )}
                    {!e.client && !e.contract && '—'}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {e.referencia}
                    {e.numeroParcela && (
                      <span className="ml-1 text-xs">
                        ({e.numeroParcela}/{e.totalParcelas})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{fmtDate(e.dataVencimento)}</td>
                  <td className="px-4 py-2 font-medium">{formatBRL(Number(e.valor))}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[e.status]}`}
                    >
                      {STATUS_LABEL[e.status]}
                    </span>
                  </td>
                  <td className="px-2 py-2 flex justify-end gap-1">
                    {e.charges && e.charges[0] && (
                      <BotaoBoleto boleto={e.charges[0]} onOpen={() => setBoletoFor(e)} />
                    )}
                    {canManage &&
                      e.tipo === 'INCOME' &&
                      (e.status === 'PENDING' || e.status === 'OVERDUE') && (
                        <button
                          type="button"
                          data-dica="Gerar cobrança"
                          onClick={() => setChargeFor(e)}
                          className="rounded-md p-2 text-primary hover:bg-primary/10"
                        >
                          <Receipt className="h-4 w-4" />
                        </button>
                      )}
                    {canManage && (e.status === 'PENDING' || e.status === 'OVERDUE') && (
                      <button
                        type="button"
                        data-dica="Marcar como pago"
                        onClick={() => markPaid.mutate(e.id)}
                        disabled={markPaid.isPending}
                        className="rounded-md p-2 text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-50"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <span>
              Página {page} de {Math.ceil(data.total / PAGE_SIZE)}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-md border border-border p-1.5 hover:bg-accent disabled:opacity-40"
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= Math.ceil(data.total / PAGE_SIZE)}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border border-border p-1.5 hover:bg-accent disabled:opacity-40"
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </section>

      {data && <p className="text-xs text-muted-foreground">{data.total} lançamento(s)</p>}

      {showCreate && (
        <CreateEntryDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}

      {chargeFor && (
        <CreateChargeDialog
          entry={chargeFor}
          onClose={() => setChargeFor(null)}
          onCreated={() => {
            setChargeFor(null);
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}

      {boletoFor && (
        <BoletoDialog
          entry={boletoFor}
          canManage={canManage}
          onClose={() => setBoletoFor(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['financial'] })}
        />
      )}

      {showCnabImport && (
        <CnabImportDialog
          onClose={() => setShowCnabImport(false)}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}

      {showDistribute && (
        <DistributeProfitsDialog
          onClose={() => setShowDistribute(false)}
          onDone={() => {
            setShowDistribute(false);
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
    </div>
  );
}

// ============================ Distribute Profits dialog ============================

interface ProfitAvailable {
  range: { from: string; to: string };
  resultadoLiquido: number;
  distribuicoesFeitas: number;
  disponivelParaDistribuir: number;
}

interface PartnerMini {
  id: string;
  fullName: string;
  percentage: string | number;
  isActive: boolean;
}

interface DistributionResult {
  parent: { id: string; valor: string | number };
  children: Array<{ id: string; partnerId: string; valor: string | number; referencia: string }>;
  totalPartners: number;
  totalDistribuido: number;
}

function DistributeProfitsDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date();
  firstDay.setUTCDate(1);
  const firstDayStr = firstDay.toISOString().slice(0, 10);

  const [from, setFrom] = useState(firstDayStr);
  const [to, setTo] = useState(today);
  const [valor, setValor] = useState(0);
  const [dataLancamento, setDataLancamento] = useState(today);
  const [referencia, setReferencia] = useState('');
  const [observacao, setObservacao] = useState('');
  const [result, setResult] = useState<DistributionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const profitQuery = useQuery({
    queryKey: ['profit-available', from, to],
    queryFn: () =>
      apiFetch<ProfitAvailable>(`/dashboard/profit-available?from=${from}&to=${to}`),
  });

  const partnersQuery = useQuery({
    queryKey: ['partners-active'],
    queryFn: () => apiFetch<PartnerMini[]>('/partners?isActive=true'),
  });

  const sumQuery = useQuery({
    queryKey: ['partners', 'sum'],
    queryFn: () => apiFetch<{ sum: number; isValid: boolean }>('/partners/percentage-sum'),
  });

  // Quando muda profit available, pre-preenche o valor com o disponivel
  const disponivel = profitQuery.data?.disponivelParaDistribuir ?? 0;
  if (disponivel > 0 && valor === 0) {
    // setState dentro do render — usa effect simulado via condicao
    // mas para evitar loop, so seta quando estiver zero
    queueMicrotask(() => setValor(disponivel));
  }

  const preview =
    partnersQuery.data?.map((p) => ({
      ...p,
      valor: Math.round(valor * (Number(p.percentage) / 100) * 100) / 100,
    })) ?? [];

  const distribute = useMutation({
    mutationFn: () =>
      apiFetch<DistributionResult>('/partners/distribute-profits', {
        method: 'POST',
        body: JSON.stringify({
          valor,
          dataLancamento,
          referencia: referencia || undefined,
          observacao: observacao || undefined,
        }),
      }),
    onSuccess: (r) => setResult(r),
    onError: (err) =>
      setError(mensagemDeErro(err)),
  });

  return (
    <DistDialog title="Distribuir lucros entre sócios" onClose={onClose}>
      {result ? (
        <div className="space-y-4">
          <div className="rounded-md bg-emerald-500/10 p-4 text-sm text-emerald-700">
            <p>
              <strong>R$ {Number(result.totalDistribuido).toFixed(2)}</strong> distribuídos para{' '}
              <strong>{result.totalPartners}</strong> sócio(s).
            </p>
            <p className="mt-1 text-xs">
              {result.children.length} lançamento(s) criado(s) no Financeiro como despesa
              (categoria Distribuição aos Sócios).
            </p>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onDone}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Concluir
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            setError(null);
            distribute.mutate();
          }}
          className="space-y-4"
        >
          {sumQuery.data && !sumQuery.data.isValid && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              ⚠ Soma dos % dos sócios ativos: <strong>{Number(sumQuery.data.sum).toFixed(2)}%</strong>{' '}
              (esperado 100%). Ajuste em Sócios antes de distribuir.
            </div>
          )}

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-2 text-xs font-medium uppercase text-muted-foreground">
              Período de apuração
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <DistField label="De">
                <input
                  type="date"
                  className="input"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </DistField>
              <DistField label="Até">
                <input
                  type="date"
                  className="input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </DistField>
            </div>
            {profitQuery.data && (
              <div className="grid grid-cols-3 gap-2 rounded-md bg-muted/30 p-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Lucro líquido</p>
                  <p className="font-medium">
                    {formatBRL(profitQuery.data.resultadoLiquido)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Já distribuído</p>
                  <p className="font-medium">
                    {formatBRL(profitQuery.data.distribuicoesFeitas)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Disponível</p>
                  <p className="font-medium text-primary">
                    {formatBRL(profitQuery.data.disponivelParaDistribuir)}
                  </p>
                </div>
              </div>
            )}
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            <DistField label="Valor a distribuir *">
              <input
                required
                type="number"
                step="0.01"
                min={0.01}
                className="input"
                value={valor || ''}
                onChange={(e) => setValor(Number(e.target.value) || 0)}
              />
            </DistField>
            <DistField label="Data do lançamento *">
              <input
                required
                type="date"
                className="input"
                value={dataLancamento}
                onChange={(e) => setDataLancamento(e.target.value)}
              />
            </DistField>
          </div>

          <DistField label="Referência (default: mês/ano)">
            <input
              className="input"
              placeholder="Ex.: Distribuição Junho/2026"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </DistField>

          <DistField label="Observação">
            <textarea
              rows={2}
              className="input"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </DistField>

          {preview.length > 0 && valor > 0 && (
            <fieldset className="rounded-md border border-primary/30 bg-primary/5 p-3">
              <legend className="px-2 text-xs font-medium uppercase text-primary">
                Preview da distribuição
              </legend>
              <table className="w-full text-sm">
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.id} className="border-b border-primary/10 last:border-0">
                      <td className="py-1">
                        {p.fullName}
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({Number(p.percentage).toFixed(2)}%)
                        </span>
                      </td>
                      <td className="py-1 text-right font-medium">{formatBRL(p.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </fieldset>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                distribute.isPending ||
                valor <= 0 ||
                !(sumQuery.data?.isValid ?? false) ||
                !(preview.length > 0)
              }
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {distribute.isPending ? 'Distribuindo…' : 'Confirmar distribuição'}
            </button>
          </div>
        </form>
      )}
      <DistStyle />
    </DistDialog>
  );
}

function DistField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function DistDialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DistStyle() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}

interface CnabImportResponse {
  bankCode: string;
  fileSequence?: number;
  fileDate?: string;
  totalEvents: number;
  applied: number;
  skipped: number;
  unrecognizedLines?: number;
  events: Array<{
    type: string;
    providerChargeId: string;
    externalReference?: string;
    amountPaid?: number;
    occurredAt?: string;
  }>;
}

function CnabImportDialog({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<CnabImportResponse | null>(null);

  const upload = useMutation({
    mutationFn: (content: string) =>
      apiFetch<CnabImportResponse>('/billing/cnab/import', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: content,
      }),
    onSuccess: (r) => {
      setResult(r);
      onImported();
    },
  });

  async function handleFile(file: File) {
    const text = await file.text();
    upload.mutate(text);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-medium">
              <FileUp className="h-5 w-5" />
              Importar arquivo CNAB de retorno
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Aceita arquivos <code>.RET</code> nos layouts CNAB 240 ou 400 — Itaú,
              Sicoob, Bradesco, BB, Caixa, Santander e outros. Baixe do Internet
              Banking e envie aqui pra conciliar os pagamentos automaticamente.
            </p>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".ret,.txt,.cnab,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
          className="block w-full text-sm"
        />

        {upload.isPending && (
          <p className="mt-3 text-sm text-muted-foreground">Processando arquivo…</p>
        )}

        {upload.isError && (
          <p className="mt-3 text-sm text-destructive">
            Erro ao processar:{' '}
            {upload.error instanceof Error ? upload.error.message : 'erro desconhecido'}
          </p>
        )}

        {result && (
          <div className="mt-4 space-y-3 text-sm">
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
                <Field label="Banco" value={result.bankCode} mono />
                <Field label="Data" value={result.fileDate ?? '—'} />
                <Field label="Sequência" value={result.fileSequence?.toString() ?? '—'} mono />
                <Field label="Total eventos" value={String(result.totalEvents)} mono />
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm">
                <strong>{result.applied}</strong> evento(s) aplicado(s).
                {result.skipped > 0 && (
                  <span className="ml-2 text-amber-700">
                    {result.skipped} ignorado(s) (boleto não localizado).
                  </span>
                )}
              </span>
            </div>

            {result.events.length > 0 && (
              <details className="rounded-md border border-border bg-muted/20 p-3">
                <summary className="cursor-pointer text-xs font-medium">
                  Ver eventos ({result.events.length})
                </summary>
                <table className="mt-2 w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left">Tipo</th>
                      <th className="text-left">Nº Banco</th>
                      <th className="text-left">Ref. interna</th>
                      <th className="text-right">Valor</th>
                      <th className="text-left">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.events.map((e, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="py-1 font-mono">{e.type.replace('BOLETO_', '')}</td>
                        <td className="font-mono text-[10px]">{e.providerChargeId}</td>
                        <td className="font-mono text-[10px]">{e.externalReference ?? '—'}</td>
                        <td className="text-right font-mono">
                          {e.amountPaid != null ? `R$ ${e.amountPaid.toFixed(2)}` : '—'}
                        </td>
                        <td className="font-mono text-[10px]">{e.occurredAt ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-xs' : 'text-xs'}>{value}</div>
    </div>
  );
}

interface BankAccountOption {
  id: string;
  name: string;
  providerName: string;
  environment: 'SANDBOX' | 'PRODUCTION';
  isDefault: boolean;
}

interface ChargeResult {
  id: string;
  providerChargeId?: string | null;
  pdfUrl?: string | null;
  linhaDigitavel?: string | null;
  pixCopiaCola?: string | null;
}

function CreateChargeDialog({
  entry,
  onClose,
  onCreated,
}: {
  entry: FinancialEntry;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [method, setMethod] = useState<'BOLETO' | 'PIX' | 'UNDEFINED'>('BOLETO');
  const [result, setResult] = useState<ChargeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => apiFetch<BankAccountOption[]>('/bank-accounts'),
  });

  const create = useMutation({
    mutationFn: () =>
      apiFetch<ChargeResult>('/billing/charge', {
        method: 'POST',
        body: JSON.stringify({
          financialEntryId: entry.id,
          bankAccountId: bankAccountId || undefined,
          method,
        }),
      }),
    onSuccess: (r) => {
      setResult(r);
    },
    onError: (err) =>
      setError(mensagemDeErro(err)),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Gerar cobrança</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-emerald-600 font-medium">
              Cobrança registrada no banco com sucesso.
            </p>
            {result.linhaDigitavel && (
              <div>
                <div className="text-xs text-muted-foreground">Linha digitável</div>
                <code className="block break-all rounded-md bg-muted/30 p-2 text-xs">
                  {result.linhaDigitavel}
                </code>
              </div>
            )}
            {result.pixCopiaCola && (
              <div>
                <div className="text-xs text-muted-foreground">PIX copia e cola</div>
                <code className="block break-all rounded-md bg-muted/30 p-2 text-xs">
                  {result.pixCopiaCola}
                </code>
              </div>
            )}
            {result.pdfUrl ? (
              <a
                href={result.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
              >
                Abrir boleto
              </a>
            ) : (
              <button
                type="button"
                onClick={() => void abrirBoletoPdf(result.id)}
                className="inline-block rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
              >
                Abrir boleto (PDF)
              </button>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onCreated}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
              >
                Concluir
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              setError(null);
              create.mutate();
            }}
            className="space-y-3"
          >
            <p className="text-sm">
              <strong>{entry.referencia}</strong> — {formatBRL(Number(entry.valor))}
            </p>
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Conta de cobrança</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
              >
                <option value="">(usar padrão)</option>
                {accounts.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.environment === 'SANDBOX' ? '(sandbox)' : ''}
                    {a.isDefault ? ' ★' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Método</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={method}
                onChange={(e) => setMethod(e.target.value as 'BOLETO' | 'PIX' | 'UNDEFINED')}
              >
                <option value="BOLETO">Boleto</option>
                <option value="PIX">PIX</option>
                <option value="UNDEFINED">Boleto + PIX (híbrido)</option>
              </select>
            </label>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={create.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {create.isPending ? 'Criando…' : 'Criar cobrança'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'good' | 'warn' | 'bad' | 'info';
}) {
  const tones: Record<typeof tone, string> = {
    good: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
    warn: 'border-amber-500/30 bg-amber-500/5 text-amber-700',
    bad: 'border-destructive/30 bg-destructive/5 text-destructive',
    info: 'border-border bg-card text-foreground',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold">{value}</div>
    </div>
  );
}

function TabButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-3 py-2 text-sm transition-colors ${
        active
          ? 'border-primary text-foreground font-medium'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function CreateEntryDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    tipo: 'INCOME' as FinancialType,
    valor: 0,
    dataVencimento: today,
    referencia: '',
    clientId: '',
    observacao: '',
  });
  const [error, setError] = useState<string | null>(null);

  const clients = useQuery({
    queryKey: ['clients', 'select'],
    queryFn: () =>
      apiFetch<{ rows: Array<{ id: string; razaoSocial: string }> }>('/clients?pageSize=200').then(
        (r) => r.rows,
      ),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        tipo: form.tipo,
        valor: form.valor,
        dataVencimento: form.dataVencimento,
        referencia: form.referencia,
      };
      if (form.clientId) payload.clientId = form.clientId;
      if (form.observacao) payload.observacao = form.observacao;
      return apiFetch<FinancialEntry>('/financial', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(mensagemDeErro(err)),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">Novo lançamento</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Tipo *</span>
              <select
                required
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.tipo}
                onChange={(e) => setForm((s) => ({ ...s, tipo: e.target.value as FinancialType }))}
              >
                <option value="INCOME">Entrada (receita)</option>
                <option value="EXPENSE">Saída (despesa)</option>
              </select>
            </label>
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Valor *</span>
              <input
                required
                type="number"
                step="0.01"
                min={0.01}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.valor || ''}
                onChange={(e) => setForm((s) => ({ ...s, valor: Number(e.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Vencimento *</span>
              <input
                required
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.dataVencimento}
                onChange={(e) => setForm((s) => ({ ...s, dataVencimento: e.target.value }))}
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-xs font-medium">Cliente</span>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.clientId}
                onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
              >
                <option value="">— nenhum —</option>
                {clients.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="space-y-1 block">
            <span className="text-xs font-medium">Referência *</span>
            <input
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.referencia}
              onChange={(e) => setForm((s) => ({ ...s, referencia: e.target.value }))}
            />
          </label>
          <label className="space-y-1 block">
            <span className="text-xs font-medium">Observação</span>
            <textarea
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.observacao}
              onChange={(e) => setForm((s) => ({ ...s, observacao: e.target.value }))}
            />
          </label>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
